import { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

// Common icon names that we know work
const COMMON_ICONS = [
  'Users', 'Award', 'Heart', 'Target', 'Home', 'Mail', 'Phone', 'MessageCircle',
  'Briefcase', 'Building', 'Calendar', 'Clock', 'Star', 'TrendingUp', 'Activity',
  'BarChart', 'PieChart', 'DollarSign', 'Gift', 'ShoppingCart', 'Package',
  'Truck', 'MapPin', 'Globe', 'Bookmark', 'Tag', 'Search', 'Filter', 'Settings',
  'Edit', 'Trash2', 'Plus', 'Minus', 'Check', 'X', 'ChevronRight', 'ChevronLeft',
  'ChevronUp', 'ChevronDown', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown',
  'Download', 'Upload', 'Share2', 'Copy', 'File', 'FileText', 'Folder', 'Image',
  'Video', 'Music', 'Headphones', 'Camera', 'Laptop', 'Smartphone', 'Tablet',
  'Monitor', 'Wifi', 'Bluetooth', 'Battery', 'Zap', 'Sun', 'Moon', 'Cloud',
  'Umbrella', 'Coffee', 'Pizza', 'Apple', 'Book', 'Newspaper', 'Lightbulb',
  'Key', 'Lock', 'Unlock', 'Shield', 'AlertCircle', 'AlertTriangle', 'Info',
  'HelpCircle', 'CheckCircle', 'XCircle', 'Bell', 'BellOff', 'Eye', 'EyeOff',
  'ThumbsUp', 'ThumbsDown', 'Smile', 'Frown', 'Meh', 'Flag', 'Bookmark',
  'Compass', 'Map', 'Navigation', 'Send', 'Inbox', 'Archive', 'RefreshCw',
  'RotateCw', 'RotateCcw', 'Maximize', 'Minimize', 'ZoomIn', 'ZoomOut',
  'Play', 'Pause', 'Square', 'Circle', 'Triangle', 'Hexagon', 'User',
  'UserCheck', 'UserPlus', 'UserMinus', 'UserX', 'Users2', 'Crown', 'Medal',
  'Rocket', 'Sparkles', 'Flame', 'Droplet', 'Wind', 'Leaf', 'Tree', 'Flower',
  'Car', 'Bus', 'Train', 'Plane', 'Ship', 'Bike', 'Dumbbell', 'Volleyball',
  'Football', 'Trophy', 'Medal', 'Ticket', 'Palette', 'Paintbrush', 'Pencil',
  'Pen', 'Eraser', 'Scissors', 'Paperclip', 'Link', 'ExternalLink', 'Anchor',
  'Hand', 'HandMetal', 'HandHeart', 'Handshake', 'HeartHandshake', 'CircleDot', 'Vote',
  'Scale', 'Building2', 'Layout', 'Stethoscope', 'GraduationCap', 'CreditCard', 
  'Mountain', 'ClipboardCheck'
];

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  onClose: () => void;
  open: boolean;
}

export function IconPicker({ value, onChange, onClose, open }: IconPickerProps) {
  const [search, setSearch] = useState('');

  const filteredIcons = COMMON_ICONS.filter((name) =>
    name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    onClose();
    setSearch('');
  };

  const renderIcon = (iconName: string) => {
    try {
      const Icon = (LucideIcons as any)[iconName];
      if (Icon) {
        return <Icon className="w-6 h-6" />;
      }
    } catch (error) {
      console.error(`Failed to render icon: ${iconName}`, error);
    }
    return <LucideIcons.HelpCircle className="w-6 h-6" />;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        setSearch('');
        onClose();
      }
    }}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>اختر أيقونة</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            placeholder="ابحث عن أيقونة... (مثال: users, heart, star)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          <ScrollArea className="h-[500px] pr-4">
            <div className="grid grid-cols-8 gap-2">
              {filteredIcons.map((iconName) => (
                <Button
                  key={iconName}
                  type="button"
                  variant={value === iconName ? 'default' : 'outline'}
                  className="h-20 w-full flex flex-col items-center justify-center gap-1 p-2"
                  onClick={() => handleSelect(iconName)}
                >
                  {renderIcon(iconName)}
                  <span className="text-[10px] truncate w-full text-center">
                    {iconName}
                  </span>
                </Button>
              ))}
            </div>
          </ScrollArea>

          {filteredIcons.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              لم يتم العثور على أيقونات
            </div>
          )}

          <div className="text-sm text-muted-foreground text-center">
            {filteredIcons.length} أيقونة متاحة
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface IconDisplayProps {
  iconName: string;
  className?: string;
}

export function IconDisplay({ iconName, className }: IconDisplayProps) {
  try {
    const Icon = (LucideIcons as any)[iconName];
    if (Icon) {
      return <Icon className={className} />;
    }
  } catch (error) {
    console.error(`Failed to display icon: ${iconName}`, error);
  }
  
  return <LucideIcons.HelpCircle className={className} />;
}

